// Pure content of the Beauty Excel export ("Exportar Excel"): which sheets, which rows, which cell formats.
// No xlsx import here on purpose, so the workbook content is unit-testable as plain arrays; the download
// itself lives in statsExport.js and the cell helpers in statsSheetsCommon.js.
//
// Cells are numbers with an Excel number format (not pre-formatted text), so the owner can sort, sum and
// chart them. A value the back could not compute (null) is an empty cell, never a 0.

import {
  buildHeatGrid,
  buildProfessionalRows,
  buildServiceRows,
  channelShares,
  choiceRows,
  deriveMetrics,
} from "./beautyStatsMetrics";
import { hours, indicatorRow, int, money, pct, rangeText, statsFileNameFor } from "./statsSheetsCommon";

// [label, formatter, metrics key] for the Resumen indicators, in reading order.
const INDICATORS = [
  ["Ocupación de agenda", pct, "occupancy"],
  ["Horas de agenda", hours, "agendaHours"],
  ["Horas con turno", hours, "bookedHours"],
  ["Horas libres en agenda", hours, "freeHours"],
  ["Turnos", int, "appointments"],
  ["Turnos atendidos", int, "attended"],
  ["Cancelados", int, "cancelled"],
  ["No asistió", int, "noShows"],
  ["Tasa de cancelación", pct, "cancellationRate"],
  ["Tasa de no asistió", pct, "noShowRate"],
  ["Ingresos cobrados", money, "revenue"],
  ["Cobrado online (seña)", money, "revenueOnline"],
  ["Cobrado en mostrador (ZocoPDV)", money, "revenuePos"],
  ["Devoluciones", money, "reversed"],
  ["Pendiente de cobro", money, "pending"],
  ["Sin cobrar por no asistió", money, "noShowUncollected"],
  ["Ticket promedio", money, "ticket"],
  ["Servicios por turno", hours, "servicesPerAppointment"],
  ["Comisión", money, "commission"],
  ["Turnos por el link del profesional", pct, "viaLinkShare"],
  ["Anticipación promedio (horas)", hours, "avgLeadHours"],
  ["Turnos pedidos el mismo día", pct, "sameDayShare"],
];

const resumenSheet = ({ current, comparison, salonName, professionalLabel }) => {
  const mine = deriveMetrics(current);
  const other = comparison ? deriveMetrics(comparison) : null;
  const withComparison = (label, format, now, then) => indicatorRow(label, format, now, then, Boolean(other));
  const channelsNow = channelShares(current.channels);
  const channelsThen = comparison ? channelShares(comparison.channels) : null;
  const choiceNow = choiceRows(current.choice);
  const choiceThen = comparison ? choiceRows(comparison.choice) : null;

  const rows = [
    ["Estadísticas de Beauty"],
    ["Salón", salonName],
    ["Período", rangeText(current)],
    ["Comparado con", comparison ? rangeText(comparison) : "Sin comparar"],
    ["Profesional", professionalLabel],
    [],
    other ? ["Indicador", "Este período", "Período de comparación"] : ["Indicador", "Este período"],
    ...INDICATORS.map(([label, format, key]) => withComparison(label, format, mine[key], other?.[key])),
  ];
  channelsNow.forEach((channel, i) => {
    rows.push(withComparison(`Turnos por ${channel.label}`, int, channel.count, channelsThen?.[i].count));
  });
  channelsNow.forEach((channel, i) => {
    rows.push(withComparison(`Ingresos por ${channel.label}`, money, channel.revenue, channelsThen?.[i].revenue));
  });
  choiceNow.forEach((row, i) => {
    rows.push(withComparison(`Eligen: ${row.label.charAt(0).toLowerCase()}${row.label.slice(1)}`, int, row.count, choiceThen?.[i].count));
  });
  return rows;
};

const diarioSheet = ({ current }) => [
  ["Fecha", "Horas de agenda", "Horas con turno", "Ocupación", "Turnos", "Ingresos cobrados"],
  ...current.daily.map((day) => [
    day.date,
    hours(day.agendaMinutes / 60),
    hours(day.bookedMinutes / 60),
    pct(day.agendaMinutes > 0 ? day.bookedMinutes / day.agendaMinutes : null),
    int(day.appointments),
    money(day.revenue),
  ]),
];

const horariosSheet = ({ current }) => {
  const grid = buildHeatGrid(current);
  return [
    ["Día", ...grid.hours.map((hour) => `${String(hour).padStart(2, "0")} h`)],
    ...grid.rows.map((row) => [row.label, ...row.cells.map((cell) => pct(cell.value))]),
  ];
};

const profesionalesSheet = ({ current }) => {
  const totals = deriveMetrics(current);
  const rows = buildProfessionalRows(current.perProfessional).map((row, i) => {
    const raw = current.perProfessional[i];
    return [
      row.name,
      row.specialty,
      int(row.appointments),
      int(raw.attended),
      hours(row.bookedHours),
      hours(raw.agendaMinutes / 60),
      pct(row.occupancy),
      money(row.revenue),
      money(row.ticket),
      money(row.commission),
      int(raw.cancelled),
      int(raw.noShows),
      pct(row.noShowRate),
      int(raw.viaLink),
      pct(row.linkShare),
    ];
  });
  const sheet = [
    [
      "Profesional",
      "Especialidad",
      "Turnos",
      "Atendidos",
      "Horas con turno",
      "Horas de agenda",
      "Ocupación de agenda",
      "Ingresos",
      "Ticket promedio",
      "Comisión",
      "Cancelados",
      "No asistió",
      "Tasa de no asistió",
      "Turnos por su link",
      "Por su link",
    ],
    ...rows,
  ];
  if (rows.length > 1) {
    sheet.push([
      "Total del salón",
      "",
      int(totals.appointments),
      int(totals.attended),
      hours(totals.bookedHours),
      hours(totals.agendaHours),
      pct(totals.occupancy),
      money(totals.revenue),
      money(totals.ticket),
      money(totals.commission),
      int(totals.cancelled),
      int(totals.noShows),
      pct(totals.noShowRate),
      int(totals.linkCount),
      pct(totals.viaLinkShare),
    ]);
  }
  return sheet;
};

// Every service of the period (no category chip applies to the export), highest revenue first. A response
// without `services` leaves just the header.
const serviciosSheet = ({ current }) => [
  ["Servicio", "Categoría", "Veces", "Duración (min)", "Facturado", "Precio promedio", "% del total"],
  ...buildServiceRows(current.services).map((row) => [
    row.name,
    row.category,
    int(row.count),
    int(row.durationMinutes),
    money(row.revenue),
    money(row.avgPrice),
    pct(row.share),
  ]),
];

/**
 * Sheets of the workbook for the current filters.
 *
 * @param {{current: object, comparison: object|null, salonName: string, professionalLabel: string,
 *   linked: boolean}} input  `current`/`comparison` are stats responses; `professionalLabel` describes
 *   the Profesional filter ("Todas", a name, or the linked professional's own name). A linked
 *   professional only has their own numbers, so the per-professional sheet is left out.
 * @returns {Array<{name: string, rows: Array<Array>}>}  Resumen, Diario, Horarios, Profesionales
 *   (not for a linked professional), Servicios.
 */
export const buildBeautySheets = (input) => [
  { name: "Resumen", rows: resumenSheet(input) },
  { name: "Diario", rows: diarioSheet(input) },
  { name: "Horarios", rows: horariosSheet(input) },
  ...(input.linked ? [] : [{ name: "Profesionales", rows: profesionalesSheet(input) }]),
  { name: "Servicios", rows: serviciosSheet(input) },
];

/** Download name: salon and period in a filesystem-safe slug. */
export const beautyFileName = ({ salonName, from, to }) =>
  statsFileNameFor({ prefix: "estadisticas-beauty", name: salonName, fallback: "salon", from, to });
